import WorkoutHistoryDetail from "@/components/fitness/WorkoutHistoryDetail";
import { freeWorkoutRouteItems } from "@/lib/fitness/freeWorkoutBuilder";
import { useFitnessResource, notifyFitnessChanged } from "@/hooks/useFitnessResource";
import { readFitnessHistory } from "@/lib/fitnessData";
import FitnessDataState from "@/components/fitness/FitnessDataState";
// FitnessHistory — chronological workout history on the generic
// ModuleHistoryList pattern (period filter + per-item edit/delete built in).
// Second consumer of the component after Body Scan; replaces the bespoke
// GymCard/ActivityCard list + custom confirm modal this page carried before.
//
// Mixed-source rules (atualizado 29/07):
// - manual gym sessions → editable (name/duration/notes) + deletable.
// - Strava / Apple Saúde / Health Connect activities → TAMBÉM editáveis e
//   apagáveis. Eram read-only porque "um import apagado voltaria no próximo
//   sync"; agora o apagar grava lápide em integration_deletions e todo sync
//   consulta as lápides antes de escrever, então a exclusão dura.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import { ChevronLeft, Dumbbell, Activity, Flame, RefreshCw } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useConnections } from "@/hooks/useConnections";
import { AgendaBottomSheet } from "@/components/agenda/CreateEventModal";
import { ModuleHistoryList } from "@/components/history/ModuleHistoryList";
import { deleteImportedRow } from "@/lib/health/deleteImported";
import {
  getSessionKind,
  getActivityDistanceKm,
  getActivityType,
  getGymSetCount,
  type WorkoutSessionRow,
} from "@/lib/fitness/sessionKind";

function formatDuration(mins: number | null | undefined, t: (k: string) => string): string {
  if (!mins || mins <= 0) return "—";
  if (mins < 60) return mins + " " + t("common.min");
  const h = Math.floor(mins / 60);
  const m = mins - h * 60;
  return m === 0 ? h + "h" : h + "h " + m + "m";
}

const Stat: React.FC<{ icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>; label: string; value: string }> = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl p-3 text-center" style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.14)" }}>
    <Icon size={13} style={{ color: "hsl(187 72% 55%)" }} />
    <p className="font-display text-[18px] font-bold text-foreground mt-1">{value}</p>
    <p className="text-[8px] font-display tracking-wide" style={{ color: "hsl(218 14% 45%)" }}>{label}</p>
  </div>
);

const FitnessHistory: React.FC = () => {
  const navigate = useNavigate();
  const { t, tf } = useLanguage();
  const { stravaConnection, syncStrava, syncing } = useConnections();

  const resource = useFitnessResource(readFitnessHistory);
  const { data: sessionData, userId, capture, refresh: load } = resource;
  const sessions = useMemo(() => sessionData ?? [], [sessionData]);
  const loading = resource.status !== "ready";
  const saving = useRef<{ isCurrent: () => boolean } | null>(null);
  const [editing, setEditing] = useState<WorkoutSessionRow | null>(null);
  const [editName, setEditName] = useState("");
  const [editDuration, setEditDuration] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const stravaActive = stravaConnection?.status === "active";

  useEffect(() => { saving.current = null; setEditing(null); setSavingEdit(false); }, [userId]);

  const handleSyncStrava = useCallback(async () => {
    const current = capture();
    if (!current()) return;
    try {
      const result = await syncStrava();
      if (!current()) return;
      toast.success(tf("fitness.history.sync.done", { count: String(result?.synced ?? 0) }));
      await load();
    } catch (err) {
      if (!current()) return;
      console.error("[FitnessHistory] sync failed:", err);
      toast.error(t("fitness.history.sync.error"));
    }
  }, [syncStrava, t, tf, load, capture]);

  const handleDelete = useCallback(async (row: WorkoutSessionRow) => {
    const current = capture();
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !current() || !user || row.user_id !== user.id) throw error ?? new Error("Fitness account changed");
      await deleteImportedRow({ table: "workout_sessions", id: row.id, userId: user.id, source: row.source, externalId: row.external_id, assertCurrent: () => { if (!current()) throw new Error("Fitness account changed"); }, requireConfirmation: true });
      if (!current()) throw new Error("Fitness account changed");
      notifyFitnessChanged(); toast.success(t("fitness.history.deleted"));
    } catch (error) {
      if (current()) toast.error(t("fitness.history.deleteError"));
      throw error; // ModuleHistoryList restores its optimistic row on failure.
    }
  }, [t, capture]);

  const openEdit = useCallback((row: WorkoutSessionRow) => {
    setEditing(row);
    setEditName(row.name || "");
    setEditDuration(row.duration_minutes != null ? String(row.duration_minutes) : "");
    setEditNotes(row.notes || "");
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!editing) return;
    const current = capture();
    if (!current() || saving.current?.isCurrent() || editing.user_id !== userId) return;
    if (!editName.trim()) {
      toast.error(t("fitness.history.edit.nameRequired"));
      return;
    }
    if (editDuration !== "" && (!Number.isFinite(Number(editDuration)) || Number(editDuration) < 0)) { toast.error(t("fitness.ux.invalidDuration")); return; }
    const lease = { isCurrent: current };
    saving.current = lease; setSavingEdit(true);
    try {
      const auth = await supabase.auth.getUser();
      if (auth.error || !current() || auth.data.user?.id !== editing.user_id) throw auth.error ?? new Error("Fitness account changed");
      const dur = Number(editDuration);
      // Carimba a correcao. `edited_at IS NOT NULL` e o que faz o sync pular
      // esta linha em vez de reescrever por cima dela na proxima rodada do
      // cron (que agora roda de 30 em 30 min, entao sem isto a correcao
      // duraria no maximo meia hora).
      const patch = {
        name: editName.trim(),
        duration_minutes: Number.isFinite(dur) && dur > 0 ? Math.round(dur) : null,
        notes: editNotes.trim() || null,
        edited_at: new Date().toISOString(),
      };
      // §4.9: chain .select() and verify a row came back.
      const { data, error } = await supabase
        .from("workout_sessions")
        .update(patch)
        .eq("id", editing.id).eq("user_id", auth.data.user.id)
        .select("id");
      if (!current()) return;
      if (error || data?.length !== 1 || data[0].id !== editing.id) {
        console.error("[FitnessHistory] edit failed or returned no row (RLS?)", error);
        toast.error(t("fitness.history.edit.saveError"));
        return;
      }
      notifyFitnessChanged();
      toast.success(t("fitness.history.edit.saved"));
      setEditing(null);
    } catch (e) {
      console.error("[FitnessHistory] edit unexpected:", e);
      if (current()) toast.error(t("fitness.history.edit.saveError"));
    } finally {
      if (saving.current === lease) { saving.current = null; if (current()) setSavingEdit(false); }
    }
  }, [editing, editName, editDuration, editNotes, t, capture, userId]);

  const counts = useMemo(() => {
    let gym = 0;
    let activity = 0;
    for (const s of sessions) {
      if (getSessionKind(s) === "activity") activity++;
      else gym++;
    }
    return { gym, activity, total: sessions.length };
  }, [sessions]);

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]" style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }} />
      </div>

      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-400 mb-4 active:opacity-70">
        <ChevronLeft size={16} /><span className="text-[13px] font-display">{t("common.back")}</span>
      </button>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
        <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("fitness.history.subtitle")}</p>
        <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">{t("fitness.history.title")}</h1>
      </motion.div>

      {!loading && counts.total > 0 && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-3 gap-2 mb-4">
          <Stat icon={Dumbbell} label={t("fitness.history.stat.gym")} value={String(counts.gym)} />
          <Stat icon={Activity} label={t("fitness.history.stat.activity")} value={String(counts.activity)} />
          <Stat icon={Flame} label={t("fitness.history.stat.total")} value={String(counts.total)} />
        </motion.div>
      )}

      {stravaActive && (
        <motion.button
          type="button"
          onClick={handleSyncStrava}
          disabled={syncing}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full mb-4 rounded-xl px-4 py-3 flex items-center justify-center gap-2 transition-colors active:scale-[0.98] disabled:opacity-60"
          style={{
            background: "hsl(20 90% 55% / 0.10)",
            border: "1px solid hsl(20 90% 55% / 0.45)",
            color: "hsl(20 90% 65%)",
          }}
        >
          <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
          <span className="font-display text-[12px] font-bold tracking-wide">
            {syncing ? t("fitness.history.sync.syncing") : t("fitness.history.sync.strava")}
          </span>
        </motion.button>
      )}

      {loading ? (
        <FitnessDataState status={resource.status} busy={resource.isRefreshing} onRetry={load} />
      ) : sessions.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <Dumbbell size={28} className="mx-auto mb-3" style={{ color: "hsl(187 72% 50% / 0.35)" }} />
          <p className="font-display text-[14px] font-semibold text-foreground/70 mb-1">{t("fitness.history.empty.title")}</p>
          <p className="text-[12px] font-body text-foreground/45 mb-5">{t("fitness.history.empty.subtitle")}</p>
          <button
            type="button"
            onClick={() => navigate("/fitness/free-workout")}
            className="px-5 py-2.5 rounded-xl font-display text-[12px] font-bold tracking-wide"
            style={{ background: "hsl(var(--primary))", color: "hsl(var(--background))" }}
          >
            {t("fitness.history.empty.cta")}
          </button>
        </motion.div>
      ) : (
        <ModuleHistoryList<WorkoutSessionRow>
          key={userId}
          items={sessions}
          getId={(s) => s.id}
          getDate={(s) => s.completed_at || s.started_at || ""}
          renderItem={(s) => {
            const kind = getSessionKind(s);
            const bits: string[] = [];
            if (s.duration_minutes) bits.push(formatDuration(s.duration_minutes, t));
            if (kind === "activity") {
              const km = getActivityDistanceKm(s);
              if (km) bits.push(km.toFixed(1) + " km");
              const type = getActivityType(s);
              if (type) bits.push(type);
            } else {
              // getGymSetCount returns { done, total } — the old `sets + " sets"`
              // concatenated the OBJECT ("[object Object] sets", TestFlight 1.0.5).
              const sets = getGymSetCount(s);
              if (sets.total > 0) bits.push(sets.done + " / " + sets.total + " " + t("fitness.workout.sets"));
            }
            return (
              <div className="min-w-0 w-full"><div className="flex items-center gap-2 min-w-0">
                <span
                  className="shrink-0 text-[9px] font-display font-bold px-1.5 py-0.5 rounded-full"
                  style={kind === "activity"
                    ? { background: "hsl(20 90% 55% / 0.15)", color: "hsl(20 90% 65%)" }
                    : { background: "hsl(187 72% 50% / 0.15)", color: "hsl(187 72% 60%)" }}
                >
                  {t(kind === "activity" ? "fitness.history.kind.activity" : "fitness.history.kind.gym")}
                </span>
                <p className="text-sm text-white truncate">
                  {(s.name || t("fitness.history.unnamed")) + (bits.length ? " · " + bits.join(" · ") : "")}
                </p>
              </div>
              {kind === "gym" && <WorkoutHistoryDetail exercises={s.exercises} onRepeat={proposal => {
                if (!userId || s.user_id !== userId) return;
                navigate("/fitness/free-workout/review", { state: { ownerId: userId, source: "history", exercises: freeWorkoutRouteItems(proposal) } });
              }} />}
              </div>
            );
          }}
          onEdit={openEdit}
          onDelete={handleDelete}
          /* Os dois gates caíram em 29/07. O motivo original — "um import
             apagado voltaria no próximo sync" — deixou de valer: a exclusão
             grava lápide e o sync a respeita. Editar import continua
             permitido de propósito (o relógio erra), e a edição nunca
             reescreve `source`, então a linha não vira manual e a chave da
             lápide continua válida. */
          labels={{
            empty: t("fitness.history.periodEmpty"),
            deletedToast: t("moduleHistory.deletedToast"),
            undo: t("moduleHistory.undo"),
            period30: t("fitness.bodyScan.period30"),
            period90: t("fitness.bodyScan.period90"),
            periodAll: t("fitness.bodyScan.periodAll"),
          }}
        />
      )}

      {/* Edit sheet — manual gym sessions only (name / duration / notes) */}
      <AgendaBottomSheet
        open={editing !== null}
        onClose={() => { if (!saving.current?.isCurrent()) setEditing(null); }}
        title={t("fitness.history.edit.title")}
        footer={
          <button
            onClick={handleEditSave}
            disabled={savingEdit}
            className="w-full py-3 rounded-xl font-display text-[14px] font-semibold"
            style={{ background: "hsl(187 72% 50%)", color: "hsl(var(--background))" }}
          >
            {savingEdit ? t("agenda.saving") : t("fitness.history.edit.save")}
          </button>
        }
      >
        <div className="space-y-3 pb-2">
          <label className="block">
            <span className="text-[11px] text-muted-foreground">{t("workout.log.name")}</span>
            <input
              disabled={savingEdit} value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg text-sm text-foreground outline-none"
              style={{ background: "hsl(222 18% 12%)", border: "1px solid hsl(222 18% 22%)" }}
            />
          </label>
          <label className="block">
            <span className="text-[11px] text-muted-foreground">{t("workout.log.duration")}</span>
            <input
              type="number"
              inputMode="numeric"
              min="0" disabled={savingEdit} value={editDuration}
              onChange={(e) => setEditDuration(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg text-sm text-foreground outline-none"
              style={{ background: "hsl(222 18% 12%)", border: "1px solid hsl(222 18% 22%)" }}
            />
          </label>
          <label className="block">
            <span className="text-[11px] text-muted-foreground">{t("fitness.history.edit.notes")}</span>
            <textarea
              rows={2}
              disabled={savingEdit} value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg text-sm text-foreground outline-none"
              style={{ background: "hsl(222 18% 12%)", border: "1px solid hsl(222 18% 22%)" }}
            />
          </label>
        </div>
      </AgendaBottomSheet>

    </MobileLayout>
  );
};

export default FitnessHistory;
